import { formatSwedishDay } from '@/lib/format'
import type { Shipment, ShipmentStatus, Subscription, SubscriptionDisplayStatus, SubscriptionFilter, SubscriptionStatus } from '@/api/types'

export type SubscriptionAction = 'withdraw' | 'cancel' | 'complete_return'

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionDisplayStatus, string> = {
  awaiting_first_delivery: 'Inväntar första leverans',
  withdrawal_window: 'Ångerperiod',
  active: 'Aktiv',
  paused: 'Pausad',
  cancelling: 'Under uppsägning',
  withdrawn: 'Ångrad',
  withdrawn_pending_return: 'Ångrad, inväntar retur',
  withdrawn_keep_first: 'Ångrad, behåller leveranser',
  terminated: 'Avslutad'
}

export const NO_SUBSCRIPTION_FILTER: SubscriptionFilter = 'none'

export const SUBSCRIPTION_FILTER_VALUES: SubscriptionFilter[] = [
  'awaiting_first_delivery',
  'withdrawal_window',
  'active',
  'paused',
  'cancelling',
  'withdrawn',
  'withdrawn_pending_return',
  'withdrawn_keep_first',
  'terminated',
  NO_SUBSCRIPTION_FILTER
]

export const SUBSCRIPTION_FILTER_LABELS: Record<SubscriptionFilter, string> = {
  ...SUBSCRIPTION_STATUS_LABELS,
  none: 'Saknar abonnemang'
}

export const SUBSCRIPTION_STATUS_STYLES: Record<SubscriptionDisplayStatus, string> = {
  awaiting_first_delivery: 'bg-blue-50 text-blue-700 border-blue-200',
  withdrawal_window: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelling: 'bg-orange-50 text-orange-700 border-orange-200',
  withdrawn: 'bg-muted text-muted-foreground border-border',
  withdrawn_pending_return: 'bg-rose-50 text-rose-700 border-rose-200',
  withdrawn_keep_first: 'bg-orange-50 text-orange-700 border-orange-200',
  terminated: 'bg-muted text-muted-foreground border-border'
}

export const SUBSCRIPTION_ACTION_LABELS: Record<SubscriptionAction, string> = {
  withdraw: 'Ångra',
  cancel: 'Säg upp',
  complete_return: 'Makulera faktura'
}

export interface FinalDeliveryOption {
  finalSequence: number | null
  label: string
  description: string
  isDefault: boolean
  takesNumber: boolean
}

const UNCOUNTED_SHIPMENT_STATUSES: ShipmentStatus[] = ['cancelled', 'returned']
const BINDING_SEQUENCE = 2
const KEPT_BY_EARLIER_WITHDRAWAL = 1

const FULL_WITHDRAWAL_OPTION: FinalDeliveryOption = {
  finalSequence: null,
  label: 'Fullständig ånger',
  description:
    'Inget behålls, inte heller leverans 1. Har paketet inte lämnats till PostNord stoppas det och ingen faktura skapas. Annars skickar kunden tillbaka paketet, och fakturan makuleras när returen tagits emot.',
  isDefault: false,
  takesNumber: false
}

export function getHighestDeliverySequence(shipments: Shipment[]): number {
  const counted = shipments.filter((shipment) => !UNCOUNTED_SHIPMENT_STATUSES.includes(shipment.status))

  return counted.reduce((highest, shipment) => Math.max(highest, shipment.sequence), 0)
}

function withdrawalWithDeliveriesOption(highestSequence: number): FinalDeliveryOption {
  return {
    finalSequence: Math.max(1, highestSequence),
    label: 'Ånger med leverans',
    description:
      'Kunden behåller och betalar leveranserna till och med det nummer ni anger. De går på sina vanliga datum och faktureras som vanligt, inget skickas därefter. Abonnemanget avslutas när den sista kommit fram och fakturorna är betalda.',
    isDefault: false,
    takesNumber: true
  }
}

export function getWithdrawalOptions(highestSequence: number): FinalDeliveryOption[] {
  return [FULL_WITHDRAWAL_OPTION, withdrawalWithDeliveriesOption(highestSequence)]
}

function standardFinalSequence(highestSequence: number): number {
  return Math.max(highestSequence, BINDING_SEQUENCE) + 1
}

function standardCancellationOption(highestSequence: number): FinalDeliveryOption {
  const finalSequence = standardFinalSequence(highestSequence)
  const deliveries =
    highestSequence < BINDING_SEQUENCE
      ? `Leverans 2 går på sin vanliga tid och leverans ${finalSequence} direkt därefter.`
      : `En sista leverans, leverans ${finalSequence}, skickas direkt.`

  return {
    finalSequence: null,
    label: `Vanlig uppsägning, sista leverans ${finalSequence}`,
    description: `${deliveries} Abonnemanget avslutas när den sista leveransen kommit fram och fakturorna är betalda.`,
    isDefault: true,
    takesNumber: false
  }
}

function chosenFinalDeliveryOption(highestSequence: number): FinalDeliveryOption {
  return {
    finalSequence: Math.max(1, highestSequence),
    label: 'Sista leverans',
    description:
      'Skriv numret på den sista leverans kunden får. Samma nummer som den senaste betyder att inget mer skickas. Leveranserna före den sista går på sina vanliga datum, den sista skickas direkt när den föregående kommit fram. Abonnemanget avslutas när allt är levererat och betalt.',
    isDefault: false,
    takesNumber: true
  }
}

export function getCancellationOptions(highestSequence: number): FinalDeliveryOption[] {
  return [standardCancellationOption(highestSequence), chosenFinalDeliveryOption(highestSequence)]
}

export function getFinalSequence(subscription: Subscription): number | null {
  if (subscription.final_sequence !== null) {
    return subscription.final_sequence
  }

  if (subscription.status === 'withdrawn_keep_first') {
    return KEPT_BY_EARLIER_WITHDRAWAL
  }

  return null
}

export function getSubscriptionAction(displayStatus: SubscriptionDisplayStatus): SubscriptionAction | null {
  if (displayStatus === 'awaiting_first_delivery' || displayStatus === 'withdrawal_window') {
    return 'withdraw'
  }

  if (displayStatus === 'active') {
    return 'cancel'
  }

  if (displayStatus === 'withdrawn_pending_return') {
    return 'complete_return'
  }

  return null
}

const FORCE_ENDABLE_STATUSES: SubscriptionDisplayStatus[] = ['cancelling', 'withdrawn_pending_return', 'withdrawn_keep_first']

export const FORCE_END_LABEL = 'Avsluta abonnemang'

export const FORCE_END_CONFIRM_MESSAGE =
  'Avsluta abonnemanget nu? Inga fler leveranser skickas, och leveranser som inte lämnats in tas bort. Fakturorna påverkas inte. Makulera dem på fakturasidan om de inte ska betalas. Det går inte att ångra.'

export function canForceEndSubscription(displayStatus: SubscriptionDisplayStatus): boolean {
  return FORCE_ENDABLE_STATUSES.includes(displayStatus)
}

const REACTIVATABLE_STATUSES: SubscriptionDisplayStatus[] = ['cancelling', 'withdrawn', 'withdrawn_pending_return', 'withdrawn_keep_first']

export const REACTIVATE_LABEL = 'Aktivera igen'

export const REACTIVATE_CONFIRM_MESSAGE =
  'Aktivera abonnemanget igen? Kunden blir aktiv och får sina leveranser som vanligt. Datumet för uppsägningen eller ångern och den valda sista leveransen tas bort. Fakturor som redan makulerats kommer inte tillbaka.'

export function canReactivateSubscription(displayStatus: SubscriptionDisplayStatus): boolean {
  return REACTIVATABLE_STATUSES.includes(displayStatus)
}

const PAUSABLE_STATUSES: SubscriptionStatus[] = ['active', 'cancelled', 'withdrawn_keep_first']
const UNSENT_STATUSES: ShipmentStatus[] = ['booking', 'booked', 'failed']

export const PAUSE_LABEL = 'Pausa'
export const RESUME_LABEL = 'Återuppta'

export const PAUSE_CONFIRM_MESSAGE =
  'Pausa abonnemanget? Inga nya leveranser skapas förrän det återupptas. En leverans som redan lämnats in faktureras som vanligt.'

export const RESUME_CONFIRM_MESSAGE =
  'Återuppta abonnemanget? Kunden får nästa leverans i tur. Har dess dag redan passerat skapas den vid nästa körning.'

export function isSubscriptionPaused(subscription: Subscription): boolean {
  return subscription.paused_at !== null
}

export function canPauseSubscription(subscription: Subscription): boolean {
  return !isSubscriptionPaused(subscription) && PAUSABLE_STATUSES.includes(subscription.status)
}

export function getUnsentShipments(shipments: Shipment[]): Shipment[] {
  return shipments.filter((shipment) => UNSENT_STATUSES.includes(shipment.status))
}

export function getRemoveUnsentOnPauseMessage(shipment: Shipment): string {
  return `Abonnemanget är pausat. Leverans ${shipment.sequence} är bokad men inte inlämnad. Vill du ta bort den också? Bokningen avbokas hos PostNord och paketet får inte lämnas in.`
}

export function getSubscriptionPauseNotice(subscription: Subscription): string | null {
  if (subscription.paused_at === null) {
    return null
  }

  return `Abonnemanget är pausat sedan ${formatSwedishDay(new Date(subscription.paused_at))}. Inga nya leveranser skapas förrän det återupptas.`
}
